import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FloatingAgentWindowGlobalStyles } from "./floating-agent-window-chrome";
import { FloatingAgentCircle } from "./floating-agent-circle";
import { FloatingAgentPanel } from "./floating-agent-panel";
import { FloatingAgentPreviewBubble } from "./floating-agent-preview-bubble";
import { useFloatingAgentReplies } from "./floating-agent-replies";
import { useFloatingAgentVoiceHold } from "./floating-agent-voice-hold";
import { FloatingAgentVoiceSessionNotice } from "./floating-agent-voice-session-notice";
import { FloatingAgentComposerRegistry } from "./floating-agent-composer-mentions";
import {
  CIRCLE_WINDOW_SIZE_PX,
  floatingStackWindowHeightPx,
  FloatingAgentPanelOpenTarget,
  maxExpandedPeekBubbleHeightPx,
  PANEL_HEIGHT_PX,
  PANEL_SHADOW_GUTTER_PX,
  PANEL_WIDTH_PX,
  PEEK_WINDOW_WIDTH_PX,
  resolveFloatingAgentOpenTarget,
} from "./floating-agent-constants";
import { SessionStatus } from "@/mds/components/sessions/session.types";

/* Hover tooltip: rendered by the desktop process in its own click-through
   window next to the circle, so no widget-window resize (and therefore no
   resize artifact) is involved. */
const TOOLTIP_HOVER_DELAY_MS = 350;
/* Hide only once the pointer has settled away, not on transient jitter. */
const TOOLTIP_HIDE_GRACE_MS = 140;
import { useAppStore } from "@/store";
import { electronModule } from "@/modules/electron";
import { FloatingAgentWindowAnchor, type FloatingAgentEvent } from "@/modules/electron/types";
import {
  getDefaultMemAgentHoldToTalkKey,
  getMemAgentHoldToTalkKeyDisplay,
} from "@/modules/mem-agent/hold-to-talk-key";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { MDSComboboxMention } from "@/mds/components/forms";
import { logger } from "@/modules/logger";

/** Resolves after the next frame has been painted (double rAF). */
const waitForPaint = (): Promise<void> =>
  new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

type WindowMode = "circle" | "panel" | "peek";

const readInteractionLeaseId = (interactionLeaseId: number | undefined): number | null =>
  typeof interactionLeaseId === "number" && Number.isSafeInteger(interactionLeaseId)
    ? interactionLeaseId
    : null;

const FLOATING_AGENT_HIT_TARGET_SELECTOR = '[data-floating-agent-hit-target="true"]';

const isFloatingAgentHitTargetAtPoint = (clientX: number, clientY: number): boolean => {
  const target = document.elementFromPoint?.(clientX, clientY) ?? null;
  return target instanceof Element && target.closest(FLOATING_AGENT_HIT_TARGET_SELECTOR) !== null;
};

const readVoiceCountdown = (
  event: FloatingAgentEvent
): { countdownStartedAtMs: number; countdownDeadlineMs: number } | null => {
  const { countdownStartedAtMs, countdownDeadlineMs } = event;
  if (
    countdownStartedAtMs === undefined ||
    countdownDeadlineMs === undefined ||
    !Number.isFinite(countdownStartedAtMs) ||
    !Number.isFinite(countdownDeadlineMs) ||
    countdownDeadlineMs <= countdownStartedAtMs
  ) {
    return null;
  }

  return { countdownStartedAtMs, countdownDeadlineMs };
};

/**
 * The desktop widget host: the OS window IS the widget, so every mode
 * change resizes the window over IPC, and PTT/summon arrive as events from
 * the main process's global key hook.
 */
export const FloatingAgentPage: FC = observer(() => {
  const { store, pageStore } = useAppStore();
  const floatingAgentPage = pageStore.floatingAgentPage;
  const isThinking = floatingAgentPage.session?.modelData?.status === SessionStatus.Processing;
  const [mode, setMode] = useState<WindowMode>("circle");
  const interactionLeaseIdRef = useRef<number | null>(null);
  const interactionLeaseAcquireGenerationRef = useRef(0);
  const interactionLeaseAcquirePromiseRef = useRef<Promise<number | null> | null>(null);
  const panelExpansionPendingRef = useRef(false);
  const anchorRef = useRef<FloatingAgentWindowAnchor>(FloatingAgentWindowAnchor.BottomRight);
  // The main window's foregrounded notes, attached to sends as implicit
  // mentions. Refetched on the desktop's change/visibility events; the main
  // process answers [] while the main window is hidden or minimized.
  const [foregroundMentions, setForegroundMentions] = useState<MDSComboboxMention[]>([]);
  const foregroundMentionsRef = useRef(foregroundMentions);
  foregroundMentionsRef.current = foregroundMentions;
  const permissionsAreApplicableForDevice =
    store.systemPermissions.permissionsAreApplicableForDevice;
  useEffect(() => {
    if (mode !== "panel") return;

    let isPassthrough = false;
    const setPointerPassthrough = (enabled: boolean) => {
      // The non-macOS native cursor poll can restore input after the pointer
      // leaves the host without a renderer event. Re-send gutter entry so a
      // stale local `true` cannot suppress the next native transition.
      if (!enabled && isPassthrough === enabled) return;
      isPassthrough = enabled;
      electronModule.floatingAgent.setPointerPassthrough({ enabled });
    };
    const handleMouseMove = (event: MouseEvent) => {
      const isInShadowGutter =
        event.clientX < PANEL_SHADOW_GUTTER_PX ||
        event.clientY < PANEL_SHADOW_GUTTER_PX ||
        event.clientX >= window.innerWidth - PANEL_SHADOW_GUTTER_PX ||
        event.clientY >= window.innerHeight - PANEL_SHADOW_GUTTER_PX;
      // Keep the host interactive for the whole gesture. In particular, the
      // visible top-left resize handle is designed to drag into this gutter.
      setPointerPassthrough(event.buttons === 0 && isInShadowGutter);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      setPointerPassthrough(false);
    };
  }, [mode]);

  useEffect(() => {
    if (mode === "panel") return;

    let isPassthrough = false;
    const setPointerPassthrough = (enabled: boolean) => {
      if (isPassthrough === enabled) return;
      isPassthrough = enabled;
      electronModule.floatingAgent.setPointerPassthrough({ enabled });
    };
    const handleMouseMove = (event: MouseEvent) => {
      if (event.buttons !== 0) {
        setPointerPassthrough(false);
        return;
      }

      setPointerPassthrough(!isFloatingAgentHitTargetAtPoint(event.clientX, event.clientY));
    };
    const handleMouseLeave = () => {
      setPointerPassthrough(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      setPointerPassthrough(false);
    };
  }, [mode]);

  const syncFloatingAgentPermissions = useCallback(async () => {
    try {
      await store.systemPermissions.syncPermissions();
    } catch (error) {
      logger.warn({
        message: "[FloatingAgentPage] permission status refresh failed",
        info: { error: String(error) },
      });
    }
  }, [store.systemPermissions]);

  const refreshForegroundContext = useCallback(async () => {
    const { notes } = await electronModule.floatingAgent.getForegroundContext();
    setForegroundMentions(
      notes.map(note => ({
        kind: MemCommonMentionKind.Note,
        // External marks the chip non-removable.
        isExternal: true,
        note_id: note.noteId,
        note_primary_label: note.label,
      }))
    );
  }, []);

  useEffect(() => {
    void refreshForegroundContext();
  }, [refreshForegroundContext]);

  useEffect(() => {
    if (!permissionsAreApplicableForDevice) {
      return;
    }

    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    void syncFloatingAgentPermissions();
    const permissionStatusSubscription = electronModule.recall.onPermissionStatus?.({
      callback: () => {
        void syncFloatingAgentPermissions();
      },
    });
    void permissionStatusSubscription
      ?.then(dispose => {
        if (!isMounted) {
          dispose?.();
          return;
        }

        unsubscribe = dispose;
      })
      .catch(error => {
        logger.warn({
          message: "[FloatingAgentPage] permission status listener failed",
          info: { error: String(error) },
        });
      });

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, [permissionsAreApplicableForDevice, syncFloatingAgentPermissions]);

  const [panelOpenTarget, setPanelOpenTarget] = useState<FloatingAgentPanelOpenTarget | null>(null);
  const [isOnboardingNudging, setIsOnboardingNudging] = useState(false);
  const [isResizingShrink, setIsResizingShrink] = useState(false);
  const [isVoiceDismissAnimating, setIsVoiceDismissAnimating] = useState(false);
  const [isWindowHidden, setIsWindowHidden] = useState(false);
  const isVoiceDismissAnimatingRef = useRef(false);
  const lastVoiceTurnCompletionIdRef = useRef(0);
  const onboardingNudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipShowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Blanked resizes are serialized so overlapping transitions can't
     interleave their blank/reveal cycles. */
  const blankedResizeChainRef = useRef<Promise<void>>(Promise.resolve());
  /* Each mounted pane registers its composer here so a PTT send targets the
     visible pane: its chips as context, and — for the thread pane, which
     mounts only while open — the thread as the reply destination. */
  const composerRegistryRef = useRef<FloatingAgentComposerRegistry>({
    main: null,
    thread: null,
  });
  const modeRef = useRef<WindowMode>("circle");
  modeRef.current = mode;
  const isPanelOpen = useCallback(() => modeRef.current === "panel", []);
  const invalidatePendingInteractionLeaseAcquire = useCallback(() => {
    interactionLeaseAcquireGenerationRef.current += 1;
    interactionLeaseAcquirePromiseRef.current = null;
  }, []);
  const updateVoiceDismissAnimation = useCallback((active: boolean) => {
    isVoiceDismissAnimatingRef.current = active;
    setIsVoiceDismissAnimating(active);
  }, []);
  const adoptInteractionLease = useCallback(
    (interactionLeaseId: number | undefined): number | null => {
      const transferredLeaseId = readInteractionLeaseId(interactionLeaseId);
      if (transferredLeaseId === null) return null;

      invalidatePendingInteractionLeaseAcquire();
      interactionLeaseIdRef.current = transferredLeaseId;
      updateVoiceDismissAnimation(false);
      return transferredLeaseId;
    },
    [invalidatePendingInteractionLeaseAcquire, updateVoiceDismissAnimation]
  );

  useEffect(
    () => () => {
      if (onboardingNudgeTimerRef.current) clearTimeout(onboardingNudgeTimerRef.current);
      isVoiceDismissAnimatingRef.current = false;
      invalidatePendingInteractionLeaseAcquire();
    },
    [invalidatePendingInteractionLeaseAcquire]
  );

  // Break the cycle: the voice-hold hook needs to open the panel, and
  // expandToPanel is defined below with its own dependencies.
  const expandToPanelRef = useRef<() => void>(() => {});

  const {
    voice,
    voiceNotice,
    setVoiceNotice,
    voiceSessionNotice,
    showSendConfirmation,
    voiceTurnCompletionId,
    isVoiceTurnActive,
    startVoiceHold,
    confirmVoiceHold,
    resolveVoiceMode,
    endVoiceHold,
    cancelOpenMic,
    recoverCanceledOpenMic,
    expireCanceledOpenMic,
    showOpenMicEndingSoon,
    clearOpenMicEndingSoon,
  } = useFloatingAgentVoiceHold({
    store,
    floatingAgentPage,
    composerRegistryRef,
    foregroundMentionsRef,
    onExpandRequest: () => expandToPanelRef.current(),
  });

  const { hasUnread, peekQueue, markRepliesSeen, dismissPeekBubble, dismissAllPeeks } =
    useFloatingAgentReplies({
      store,
      floatingAgentPage,
      isPanelOpen: mode === "panel",
      // The desktop widget is its own BrowserWindow renderer, so its tab
      // lifecycle reflects that window's OS focus.
      isFocused: store.tabLifecycleManager.isTabActive,
    });

  useEffect(() => {
    void electronModule.floatingAgent.setUnreadState({ hasUnread });
  }, [hasUnread]);

  // Provision the assistant, adopt its existing session, and start the live view.
  useEffect(() => {
    void floatingAgentPage.initialize();
  }, [floatingAgentPage]);

  /**
   * Resizes the window with nothing visible. Whatever the paint/resize
   * ordering, the OS composites one artifact frame (the stale buffer
   * displaced to the new origin, or the new layout clipped to the old
   * bounds) — so: blank the window, PROVE the blank frame painted, apply
   * state + resize invisibly, then reveal (content animates itself in).
   */
  const runBlankedResize = useCallback((run: () => Promise<void>): Promise<void> => {
    const chained = blankedResizeChainRef.current.then(async () => {
      flushSync(() => setIsWindowHidden(true));
      await waitForPaint();
      try {
        await run();
        // Let the new layout paint (still blanked) so the reveal starts
        // complete.
        await waitForPaint();
      } finally {
        setIsWindowHidden(false);
      }
    });
    blankedResizeChainRef.current = chained.catch(() => {});
    return chained;
  }, []);

  const acquireInteractionLease = useCallback((): Promise<number | null> => {
    const pendingAcquire = interactionLeaseAcquirePromiseRef.current;
    if (pendingAcquire) return pendingAcquire;

    const acquireGeneration = interactionLeaseAcquireGenerationRef.current;
    const acquirePromise = (async () => {
      const result = await electronModule.floatingAgent.setInteractionActive({ active: true });
      const acquiredLeaseId = readInteractionLeaseId(result?.interactionLeaseId);
      if (acquiredLeaseId === null) return null;

      if (interactionLeaseAcquireGenerationRef.current !== acquireGeneration) {
        await electronModule.floatingAgent.setInteractionActive({
          active: false,
          interactionLeaseId: acquiredLeaseId,
        });
        return null;
      }

      interactionLeaseIdRef.current = acquiredLeaseId;
      return acquiredLeaseId;
    })();

    interactionLeaseAcquirePromiseRef.current = acquirePromise;
    const clearPendingAcquire = () => {
      if (interactionLeaseAcquirePromiseRef.current === acquirePromise) {
        interactionLeaseAcquirePromiseRef.current = null;
      }
    };
    void acquirePromise.then(clearPendingAcquire, clearPendingAcquire);
    return acquirePromise;
  }, []);

  const releaseInteractionLease = useCallback(async (interactionLeaseId: number): Promise<void> => {
    await electronModule.floatingAgent.setInteractionActive({
      active: false,
      interactionLeaseId,
    });
    if (interactionLeaseIdRef.current === interactionLeaseId) {
      interactionLeaseIdRef.current = null;
    }
  }, []);

  const finishVoiceOnlyInteraction = useCallback(() => {
    if (
      modeRef.current === "panel" ||
      panelExpansionPendingRef.current ||
      interactionLeaseAcquirePromiseRef.current !== null
    ) {
      return;
    }

    const interactionLeaseId = interactionLeaseIdRef.current;
    if (interactionLeaseId === null) return;

    // Always-visible agents and unread replies still have a reason to remain
    // on screen. Drop only the voice lease; contextual presence keeps the
    // presentation visible.
    if (store.floatingAgent.presence === "always" || hasUnread) {
      void releaseInteractionLease(interactionLeaseId);
      return;
    }
    updateVoiceDismissAnimation(true);
  }, [
    hasUnread,
    releaseInteractionLease,
    store.floatingAgent.presence,
    updateVoiceDismissAnimation,
  ]);

  // Arm the terminal dismissal frame before the browser paints the render
  // that clears the send checkmark. A passive effect can expose the normal
  // Mem mark for one frame between those two visual states.
  useLayoutEffect(() => {
    if (voiceTurnCompletionId === lastVoiceTurnCompletionIdRef.current) {
      return;
    }
    lastVoiceTurnCompletionIdRef.current = voiceTurnCompletionId;
    if (voiceTurnCompletionId === 0 || isVoiceTurnActive) return;

    finishVoiceOnlyInteraction();
  }, [finishVoiceOnlyInteraction, isVoiceTurnActive, voiceTurnCompletionId]);

  useEffect(() => {
    if (!isVoiceDismissAnimating || (store.floatingAgent.presence !== "always" && !hasUnread)) {
      return;
    }

    // A reply can become unread while the shrink is running. Restore the
    // circle and release only the interaction reason; unread/always presence
    // keeps the presentation visible.
    updateVoiceDismissAnimation(false);
    const interactionLeaseId = interactionLeaseIdRef.current;
    if (interactionLeaseId !== null) {
      void releaseInteractionLease(interactionLeaseId);
    }
  }, [
    hasUnread,
    isVoiceDismissAnimating,
    releaseInteractionLease,
    store.floatingAgent.presence,
    updateVoiceDismissAnimation,
  ]);

  /** Tears the tooltip window down and cancels any pending show/hide. */
  const hideCircleTooltip = useCallback(() => {
    if (tooltipShowTimerRef.current) {
      clearTimeout(tooltipShowTimerRef.current);
      tooltipShowTimerRef.current = null;
    }
    if (tooltipHideTimerRef.current) {
      clearTimeout(tooltipHideTimerRef.current);
      tooltipHideTimerRef.current = null;
    }
    void electronModule.floatingAgent.tooltipHide();
  }, []);

  const expandToPanel = useCallback(
    async (target?: FloatingAgentPanelOpenTarget, transferredInteractionLeaseId?: number) => {
      updateVoiceDismissAnimation(false);
      const transferredLeaseId = adoptInteractionLease(transferredInteractionLeaseId);
      if (isPanelOpen()) return;
      // Acquire the interaction lease before advancing the unread watermark;
      // otherwise "When unread" could hide the window during its own grow.
      if (transferredLeaseId === null) {
        const acquiredLeaseId = await acquireInteractionLease();
        if (acquiredLeaseId === null) return;
      }

      // Voice confirmation can finish while the lease IPC is pending and
      // re-arm the circle's dismiss animation. Opening chat wins that race:
      // clear dismissal again at the async boundary before mounting the panel.
      updateVoiceDismissAnimation(false);
      if (isPanelOpen() || panelExpansionPendingRef.current) return;

      panelExpansionPendingRef.current = true;
      // Resolved before markRepliesSeen() below advances the watermark.
      setPanelOpenTarget(resolveFloatingAgentOpenTarget(floatingAgentPage, target));
      hideCircleTooltip();
      floatingAgentPage.completeFirstMessageOnboardingIfNeeded("panel-open");
      try {
        await runBlankedResize(async () => {
          // Enter panel mode before the grow resize: marking replies seen
          // empties the peek queue, and the queue effect would otherwise see a
          // still-"peek" mode with an empty queue and fight this transition
          // with a collapse.
          updateVoiceDismissAnimation(false);
          setMode("panel");
          modeRef.current = "panel";
          markRepliesSeen();
          await electronModule.floatingAgent.resize({
            width: PANEL_WIDTH_PX,
            height: PANEL_HEIGHT_PX,
            mode: "panel",
            anchor: anchorRef.current,
          });
        });
      } finally {
        panelExpansionPendingRef.current = false;
      }
    },
    [
      acquireInteractionLease,
      adoptInteractionLease,
      floatingAgentPage,
      hideCircleTooltip,
      isPanelOpen,
      markRepliesSeen,
      runBlankedResize,
      updateVoiceDismissAnimation,
    ]
  );
  expandToPanelRef.current = () => void expandToPanel();

  const handleCircleHoverChange = useCallback(
    (hovering: boolean) => {
      if (!hovering) {
        if (tooltipShowTimerRef.current) {
          clearTimeout(tooltipShowTimerRef.current);
          tooltipShowTimerRef.current = null;
        }
        // Grace period instead of an immediate hide, so transient pointer
        // jitter off the circle doesn't tear the tooltip down.
        if (!tooltipHideTimerRef.current) {
          tooltipHideTimerRef.current = setTimeout(() => {
            tooltipHideTimerRef.current = null;
            void electronModule.floatingAgent.tooltipHide();
          }, TOOLTIP_HIDE_GRACE_MS);
        }
        return;
      }
      if (tooltipHideTimerRef.current) {
        clearTimeout(tooltipHideTimerRef.current);
        tooltipHideTimerRef.current = null;
      }
      if (modeRef.current !== "circle" || voice.isListening) return;
      if (tooltipShowTimerRef.current) return;
      tooltipShowTimerRef.current = setTimeout(() => {
        tooltipShowTimerRef.current = null;
        if (modeRef.current !== "circle" || voice.isListening) return;
        void electronModule.floatingAgent.tooltipShow();
      }, TOOLTIP_HOVER_DELAY_MS);
    },
    [voice]
  );

  const collapseToCircle = useCallback(async () => {
    const interactionLeaseId = interactionLeaseIdRef.current;
    invalidatePendingInteractionLeaseAcquire();
    // Hide the circle chrome for the shrink frame — the OS animates the
    // window bounds, and a visible circle would ghost at the old position.
    setIsResizingShrink(true);
    modeRef.current = "circle";
    setMode("circle");
    // Dismiss whatever was on screen so it doesn't immediately re-pop; the
    // unread dot stays until the panel is opened.
    dismissAllPeeks();
    await electronModule.floatingAgent.resize({
      width: CIRCLE_WINDOW_SIZE_PX,
      height: CIRCLE_WINDOW_SIZE_PX,
      mode: "circle",
      anchor: anchorRef.current,
    });
    if (interactionLeaseId !== null) {
      await releaseInteractionLease(interactionLeaseId);
    }
    requestAnimationFrame(() => setIsResizingShrink(false));
  }, [dismissAllPeeks, invalidatePendingInteractionLeaseAcquire, releaseInteractionLease]);

  const completeVoiceDismissAnimation = useCallback(() => {
    if (!isVoiceDismissAnimatingRef.current || modeRef.current === "panel") return;

    const interactionLeaseId = interactionLeaseIdRef.current;
    if (interactionLeaseId === null) {
      updateVoiceDismissAnimation(false);
      return;
    }

    // Keep the animation's terminal frame painted until the main process has
    // hidden the native window. Clearing it sooner can flash the full circle
    // between animationend and the asynchronous interaction-release IPC.
    void releaseInteractionLease(interactionLeaseId)
      .then(() => {
        if (
          interactionLeaseIdRef.current !== null ||
          modeRef.current === "panel" ||
          !isVoiceDismissAnimatingRef.current
        ) {
          return;
        }
        updateVoiceDismissAnimation(false);
      })
      .catch(error => {
        logger.warn({
          message: "[FloatingAgentPage] failed to release dismissed voice interaction",
          info: { error: String(error) },
        });
      });
  }, [releaseInteractionLease, updateVoiceDismissAnimation]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      // The global key hook owns Escape while open-mic cancellation or its
      // undo window is active. Keep the focused renderer from also collapsing
      // the panel on the same physical key press.
      if (voice.state !== "idle" || voiceSessionNotice?.kind === "canceled") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (modeRef.current !== "panel") return;

      event.preventDefault();
      event.stopPropagation();
      void collapseToCircle();
    };

    // This renderer receives key events only while its BrowserWindow is
    // focused, so Escape remains local rather than becoming a global hotkey.
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [collapseToCircle, voice.state, voiceSessionNotice?.kind]);

  /* Dragging repositions the window and snaps it to a corner, so the peek
     stack has to go: its taller bounds would snap as the widget's size. The
     unread dot stays until the panel is opened. */
  const handleDragStart = useCallback(() => {
    hideCircleTooltip();
    if (modeRef.current === "peek") void collapseToCircle();
  }, [hideCircleTooltip, collapseToCircle]);

  // Peek window management: the stack of undismissed unseen replies decides
  // the mode and the window height (bubbles queue up and persist on screen).
  const peekQueueLength = peekQueue.length;
  const visiblePeekBubbleCount = peekQueueLength;
  const hasVoiceSessionNotice = voiceSessionNotice !== null;

  /* Height the hovered bubble wants, or null when nothing is expanded. The
     native window has to grow to match — CSS alone cannot paint outside it. */
  const [expandedBubble, setExpandedBubble] = useState<{
    eventId: string;
    heightPx: number;
  } | null>(null);
  const expandedBubbleHeightPx = expandedBubble?.heightPx ?? null;

  /* screen.availHeight is the work area of the display hosting this window,
     already excluding the menu bar and dock. */
  const maxExpandedBubbleHeightPx = maxExpandedPeekBubbleHeightPx({
    availableHeightPx: window.screen.availHeight,
    bubbleCount: visiblePeekBubbleCount,
    hasVoiceSessionNotice,
  });

  const handleExpandedBubbleHeightChange = useCallback(
    (eventId: string, heightPx: number | null) => {
      setExpandedBubble(currentBubble => {
        if (heightPx !== null) return { eventId, heightPx };
        if (currentBubble?.eventId !== eventId) return currentBubble;

        return null;
      });
    },
    []
  );

  useEffect(() => {
    if (modeRef.current === "panel") return;

    if (visiblePeekBubbleCount > 0 || hasVoiceSessionNotice) {
      // The peek stack owns the window size now — drop the tooltip without
      // the shrink it would otherwise schedule.
      hideCircleTooltip();
      let stale = false;
      void electronModule.floatingAgent
        .resize({
          width: PEEK_WINDOW_WIDTH_PX,
          height: floatingStackWindowHeightPx({
            bubbleCount: visiblePeekBubbleCount,
            hasVoiceSessionNotice,
            expandedBubbleHeightPx,
          }),
          mode: "peek",
          anchor: anchorRef.current,
        })
        .then(() => {
          if (stale || modeRef.current === "panel") return;
          modeRef.current = "peek";
          setMode("peek");
        });
      return () => {
        stale = true;
      };
    } else if (modeRef.current === "peek") {
      void collapseToCircle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePeekBubbleCount, hasVoiceSessionNotice, expandedBubbleHeightPx]);

  useEffect(() => {
    void electronModule.floatingAgent.getAnchor().then(anchor => {
      if (anchor) anchorRef.current = anchor;
    });

    const dispose = electronModule.floatingAgent.onEvent(event => {
      if (event.kind === "summon") {
        // The summon key toggles: an open panel collapses back to the circle.
        if (isPanelOpen()) {
          void collapseToCircle();
          return;
        }
        // A selection sent from the main window is held by the main process.
        // Pull it here rather than inside expandToPanel: that early-returns
        // when the panel is already open, which would drop the request.
        void store.floatingAgent.adoptPendingComposerMentions();
        void expandToPanel(event.panelOpenTarget, event.interactionLeaseId);
      } else if (event.kind === "onboarding-nudge") {
        if (onboardingNudgeTimerRef.current) clearTimeout(onboardingNudgeTimerRef.current);
        setIsOnboardingNudging(true);
        onboardingNudgeTimerRef.current = setTimeout(() => {
          setIsOnboardingNudging(false);
          onboardingNudgeTimerRef.current = null;
        }, 5_000);
      } else if (event.kind === "anchor-changed" && event.anchor) {
        anchorRef.current = event.anchor;
      } else if (event.kind === "voice-hold-start") {
        adoptInteractionLease(event.interactionLeaseId);
        hideCircleTooltip();
        updateVoiceDismissAnimation(false);
        startVoiceHold();
      } else if (event.kind === "voice-hold-confirmed") {
        if (
          typeof event.voiceSessionId === "number" &&
          Number.isSafeInteger(event.voiceSessionId) &&
          (event.voiceMode === undefined ||
            event.voiceMode === "ptt" ||
            event.voiceMode === "hands-free")
        ) {
          confirmVoiceHold({
            voiceSessionId: event.voiceSessionId,
            mode: event.voiceMode,
          });
        }
      } else if (event.kind === "voice-hold-mode-resolved") {
        if (
          typeof event.voiceSessionId === "number" &&
          Number.isSafeInteger(event.voiceSessionId) &&
          (event.voiceMode === "ptt" || event.voiceMode === "hands-free")
        ) {
          resolveVoiceMode({
            voiceSessionId: event.voiceSessionId,
            mode: event.voiceMode,
          });
        }
      } else if (event.kind === "voice-hold-end") {
        void endVoiceHold({ canceled: event.canceled === true });
      } else if (event.kind === "voice-open-mic-canceled") {
        const countdown = readVoiceCountdown(event);
        if (countdown) cancelOpenMic(countdown);
      } else if (event.kind === "voice-open-mic-recover") {
        void recoverCanceledOpenMic();
      } else if (event.kind === "voice-open-mic-cancel-expired") {
        expireCanceledOpenMic();
      } else if (event.kind === "voice-open-mic-ending-soon") {
        const countdown = readVoiceCountdown(event);
        if (countdown) showOpenMicEndingSoon(countdown);
      } else if (event.kind === "voice-open-mic-continued") {
        clearOpenMicEndingSoon();
      } else if (
        event.kind === "foreground-context-changed" ||
        event.kind === "main-window-shown" ||
        event.kind === "main-window-hidden"
      ) {
        // Visibility changes flip what the main process reports, so the
        // window events refetch alongside explicit context changes.
        void refreshForegroundContext();
      }
    });
    electronModule.floatingAgent.rendererReady();
    return dispose;
  }, [
    expandToPanel,
    collapseToCircle,
    isPanelOpen,
    adoptInteractionLease,
    hideCircleTooltip,
    updateVoiceDismissAnimation,
    startVoiceHold,
    confirmVoiceHold,
    resolveVoiceMode,
    endVoiceHold,
    cancelOpenMic,
    recoverCanceledOpenMic,
    expireCanceledOpenMic,
    showOpenMicEndingSoon,
    clearOpenMicEndingSoon,
    refreshForegroundContext,
    store,
  ]);

  const isTopAnchored =
    anchorRef.current === FloatingAgentWindowAnchor.TopLeft ||
    anchorRef.current === FloatingAgentWindowAnchor.TopRight;
  const isLeftAnchored =
    anchorRef.current === FloatingAgentWindowAnchor.TopLeft ||
    anchorRef.current === FloatingAgentWindowAnchor.BottomLeft;
  const hotkeyLabel = store.spaceAccountSettings.memAgent.holdToTalkIsEnabled
    ? getMemAgentHoldToTalkKeyDisplay(
        store.spaceAccountSettings.memAgent.holdToTalkKey ?? getDefaultMemAgentHoldToTalkKey()
      ).tooltipKey
    : "";
  const voiceSessionNoticeCard = voiceSessionNotice ? (
    <FloatingAgentVoiceSessionNotice notice={voiceSessionNotice} hotkeyLabel={hotkeyLabel} />
  ) : null;

  return (
    <>
      <FloatingAgentWindowGlobalStyles />
      <StyledWindowContent
        $hidden={isWindowHidden}
        $isTopAnchored={isTopAnchored}
        $isLeftAnchored={isLeftAnchored}
      >
        {mode === "panel" ? (
          <>
            <FloatingAgentPanel
              floatingAgentPage={floatingAgentPage}
              voice={voice}
              voiceNotice={voiceNotice}
              composerRegistryRef={composerRegistryRef}
              openTarget={panelOpenTarget}
              foregroundMentions={foregroundMentions}
              onDismissVoiceNotice={() => setVoiceNotice(null)}
              onCollapse={collapseToCircle}
              onCancelVoice={() => void endVoiceHold({ canceled: true })}
              onConfirmVoice={() => void endVoiceHold()}
            />
            {voiceSessionNoticeCard && (
              <StyledPanelVoiceSessionNotice>
                {voiceSessionNoticeCard}
              </StyledPanelVoiceSessionNotice>
            )}
          </>
        ) : (
          <StyledPeekLayout $isTopAnchored={isTopAnchored} $isLeftAnchored={isLeftAnchored}>
            {/* The circle hugs its anchored window edge; bubbles queue on the
              circle's inward side, newest adjacent (tail points at the
              circle). Top anchors mirror the stack. */}
            {isTopAnchored && (
              <FloatingAgentCircle
                voice={voice}
                showSendConfirmation={showSendConfirmation}
                isResizingShrink={isResizingShrink}
                isDismissing={isVoiceDismissAnimating}
                hasUnread={hasUnread}
                isThinking={isThinking}
                isOnboardingNudging={isOnboardingNudging}
                onExpand={() => void expandToPanel()}
                onHoverChange={handleCircleHoverChange}
                onDragStart={handleDragStart}
                onDragMove={cursor => electronModule.floatingAgent.dragMove(cursor)}
                onDragEnd={cursor => void electronModule.floatingAgent.dragEnd(cursor)}
                onDismissAnimationEnd={completeVoiceDismissAnimation}
              />
            )}
            {mode === "peek" && isTopAnchored && voiceSessionNoticeCard && (
              <StyledStackVoiceSessionNotice $isLeftAnchored={isLeftAnchored}>
                {voiceSessionNoticeCard}
              </StyledStackVoiceSessionNotice>
            )}
            {mode === "peek" &&
              (isTopAnchored ? [...peekQueue].reverse() : peekQueue).map((reply, index, stack) => (
                <FloatingAgentPreviewBubble
                  key={reply.eventId}
                  content={reply.text}
                  expansionKey={reply.eventId}
                  anchor={anchorRef.current}
                  showTail={index === (isTopAnchored ? 0 : stack.length - 1)}
                  maxExpandedHeightPx={maxExpandedBubbleHeightPx}
                  onExpandedHeightChange={handleExpandedBubbleHeightChange}
                  onOpen={() =>
                    void expandToPanel(
                      {
                        eventId: reply.eventId,
                        threadRootId:
                          floatingAgentPage.threadAnchorByEventId.get(reply.eventId) ?? null,
                      },
                      undefined
                    )
                  }
                  onDismiss={() => dismissPeekBubble(reply.eventId)}
                />
              ))}
            {mode === "peek" && !isTopAnchored && voiceSessionNoticeCard && (
              <StyledStackVoiceSessionNotice $isLeftAnchored={isLeftAnchored}>
                {voiceSessionNoticeCard}
              </StyledStackVoiceSessionNotice>
            )}
            {!isTopAnchored && (
              <FloatingAgentCircle
                voice={voice}
                showSendConfirmation={showSendConfirmation}
                isResizingShrink={isResizingShrink}
                isDismissing={isVoiceDismissAnimating}
                hasUnread={hasUnread}
                isThinking={isThinking}
                isOnboardingNudging={isOnboardingNudging}
                onExpand={() => void expandToPanel()}
                onHoverChange={handleCircleHoverChange}
                onDragStart={handleDragStart}
                onDragMove={cursor => electronModule.floatingAgent.dragMove(cursor)}
                onDragEnd={cursor => void electronModule.floatingAgent.dragEnd(cursor)}
                onDismissAnimationEnd={completeVoiceDismissAnimation}
              />
            )}
          </StyledPeekLayout>
        )}
      </StyledWindowContent>
    </>
  );
});

const StyledPeekLayout = styled.div<{
  $isTopAnchored: boolean;
  $isLeftAnchored: boolean;
}>`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isLeftAnchored }) => ($isLeftAnchored ? "flex-start" : "flex-end")};
  /* Pin the stack to the circle's anchored window edge. */
  justify-content: ${({ $isTopAnchored }) => ($isTopAnchored ? "flex-start" : "flex-end")};
  gap: 6px;
`;

const StyledStackVoiceSessionNotice = styled.div<{ $isLeftAnchored: boolean }>`
  ${({ $isLeftAnchored }) => ($isLeftAnchored ? "margin-left: 16px;" : "margin-right: 16px;")}
`;

const StyledPanelVoiceSessionNotice = styled.div`
  position: fixed;
  top: ${PANEL_SHADOW_GUTTER_PX + 52}px;
  left: 50%;
  z-index: 2;
  transform: translateX(-50%);
  pointer-events: none;
`;

/**
 * Blanks the whole window during bounds changes (see expandToPanel): hiding
 * is instant (no transition) so the pre-resize blank frame is truly empty;
 * the reveal is animated by the mounted content itself.
 */
const StyledWindowContent = styled.div<{
  $hidden: boolean;
  $isTopAnchored: boolean;
  $isLeftAnchored: boolean;
}>`
  display: contents;
  --floating-agent-enter-origin: ${({ $isTopAnchored, $isLeftAnchored }) =>
    `${$isTopAnchored ? "top" : "bottom"} ${$isLeftAnchored ? "left" : "right"}`};

  ${({ $hidden }) =>
    $hidden &&
    `
    > * {
      opacity: 0 !important;
      transition: none !important;
      animation: none !important;
    }
  `}
`;
